import { useCallback, useEffect, useMemo, useState } from "react";
import { mapApiError } from "../../components/ui/StatusMessage";
import { useToast } from "../../components/ui/Toast";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  HelpCircle,
  Info,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldAlert,
  Sparkles,
  Trash2,
  UserCheck,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  api,
  ApiError,
  type AdminEntitlementItem,
  type AdminPackageCreate,
  type AdminPremiumPackage,
  type AdminUser,
} from "../../lib/api";
import { canAccess } from "../../lib/auth-tokens";
import { ConfirmDialog, ModalShell } from "../../components/admin/AdminModals";
import { AdminEmptyState, AdminFilterBar, AdminPageHeader, AdminTabBar } from "../../components/admin/AdminUI";

function useAdminToken() {
  const { tokens } = useAuth();
  return tokens?.accessToken ?? null;
}

export function AdminPackagesPage() {
  const { showToast } = useToast();
  const { tokens } = useAuth();
  const token = useAdminToken();
  const canWrite = canAccess(tokens, "payments.write");

  const [activeTab, setActiveTab] = useState<"packages" | "entitlements">("packages");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);

  // Packages state
  const [packages, setPackages] = useState<AdminPremiumPackage[]>([]);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<AdminPremiumPackage | null>(null);
  const [packageDeleteTarget, setPackageDeleteTarget] = useState<AdminPremiumPackage | null>(null);

  // Package form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPlanType, setFormPlanType] = useState<"monthly" | "semester">("semester");
  const [formStream, setFormStream] = useState<string>("");
  const [formSemesters, setFormSemesters] = useState<number[]>([1]);
  const [formPrice, setFormPrice] = useState<string>("499");
  const [formDurationMonths, setFormDurationMonths] = useState<string>("1");
  const [formFeatures, setFormFeatures] = useState<string[]>([""]);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formDisplayOrder, setFormDisplayOrder] = useState<string>("0");
  const [formError, setFormError] = useState("");

  // Entitlements state
  const [entitlements, setEntitlements] = useState<AdminEntitlementItem[]>([]);
  const [entSearch, setEntSearch] = useState("");
  const [entStatusFilter, setEntStatusFilter] = useState("all");
  const [entStreamFilter, setEntStreamFilter] = useState("all");
  const [entSemesterFilter, setEntSemesterFilter] = useState("all");
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AdminEntitlementItem | null>(null);
  const [revokeReason, setRevokeReason] = useState("");

  // Grant form state
  const [studentSearchQuery, setStudentSearchQuery] = useState("");
  const [studentSearchResults, setStudentSearchResults] = useState<AdminUser[]>([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<AdminUser | null>(null);
  const [grantStream, setGrantStream] = useState("common");
  const [grantSemester, setGrantSemester] = useState<number>(1);
  const [grantDurationMonths, setGrantDurationMonths] = useState("1");
  const [grantPackageId, setGrantPackageId] = useState<string>("");
  const [grantReason, setGrantReason] = useState("");
  const [grantError, setGrantError] = useState("");

  const showNotice = (msg: string) => {
    setSuccessNotice(msg);
    setTimeout(() => setSuccessNotice(null), 4500);
  };

  // Load packages
  const loadPackages = useCallback(async () => {
    if (!token) return;
    try {
      setError(null);
      const res = await api.admin.packages.list(token);
      setPackages(res.items || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load packages");
    }
  }, [token]);

  // Load entitlements
  const loadEntitlements = useCallback(async () => {
    if (!token) return;
    try {
      setError(null);
      const res = await api.admin.entitlements.list(token, {
        search: entSearch.trim() || undefined,
        status: entStatusFilter !== "all" ? entStatusFilter : undefined,
        stream: entStreamFilter !== "all" ? entStreamFilter : undefined,
        semester: entSemesterFilter !== "all" ? Number(entSemesterFilter) : undefined,
      });
      setEntitlements(res.items || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load entitlements");
    }
  }, [token, entSearch, entStatusFilter, entStreamFilter, entSemesterFilter]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      if (activeTab === "packages") {
        await loadPackages();
      } else {
        await loadEntitlements();
      }
      setLoading(false);
    }
    init();
  }, [activeTab, loadPackages, loadEntitlements]);

  // Search students when granting
  useEffect(() => {
    if (!studentSearchQuery.trim() || !token || selectedStudent) {
      setStudentSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingStudents(true);
      try {
        const res = await api.admin.users(token, { search: studentSearchQuery.trim() });
        setStudentSearchResults(res.items.slice(0, 8));
      } catch {
        setStudentSearchResults([]);
      } finally {
        setSearchingStudents(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [studentSearchQuery, token, selectedStudent]);

  // Open create package modal
  const handleOpenCreatePackage = () => {
    setEditingPackage(null);
    setFormName("");
    setFormDescription("");
    setFormPlanType("semester");
    setFormStream("");
    setFormSemesters([1]);
    setFormPrice("499");
    setFormDurationMonths("1");
    setFormFeatures(["Full Semester 1 access", "Mid & Final past exams with AI explanations", "Unlimited AI tutor questions"]);
    setFormIsActive(true);
    setFormDisplayOrder(String(packages.length + 1));
    setFormError("");
    setPackageModalOpen(true);
  };

  // Open edit package modal
  const handleOpenEditPackage = (pkg: AdminPremiumPackage) => {
    setEditingPackage(pkg);
    setFormName(pkg.name);
    setFormDescription(pkg.description || "");
    setFormPlanType((pkg.plan_type || pkg.planType || "semester") as "monthly" | "semester");
    setFormStream(pkg.stream || "");
    setFormSemesters(pkg.semesters_covered || pkg.semestersCovered || [1]);
    setFormPrice(String(pkg.price));
    setFormDurationMonths(String(pkg.duration_months || pkg.durationMonths || 1));
    setFormFeatures(pkg.features && pkg.features.length > 0 ? [...pkg.features] : [""]);
    setFormIsActive(pkg.is_active ?? pkg.isActive ?? true);
    setFormDisplayOrder(String(pkg.display_order ?? pkg.displayOrder ?? 0));
    setFormError("");
    setPackageModalOpen(true);
  };

  // Toggle package active status directly from table
  const handleTogglePackageActive = async (pkg: AdminPremiumPackage) => {
    if (!token || !canWrite || busy) return;
    const currentActive = pkg.is_active ?? pkg.isActive ?? true;
    setBusy(true);
    try {
      await api.admin.packages.update(token, pkg.id, { is_active: !currentActive });
      setPackages((prev) =>
        prev.map((p) => (p.id === pkg.id ? { ...p, is_active: !currentActive, isActive: !currentActive } : p))
      );
      showNotice(`Package '${pkg.name}' ${!currentActive ? "activated" : "deactivated"}.`);
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // Move package up/down in display order
  const handleMovePackage = async (index: number, direction: "up" | "down") => {
    if (!token || !canWrite || busy) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= packages.length) return;

    const newPackages = [...packages];
    const [moved] = newPackages.splice(index, 1);
    newPackages.splice(targetIndex, 0, moved);

    // Reassign display_order sequentially
    const payload = newPackages.map((p, idx) => ({ id: p.id, display_order: idx + 1 }));
    setPackages(newPackages);
    setBusy(true);
    try {
      await api.admin.packages.reorder(token, payload);
    } catch (err) {
      await loadPackages();
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // Save Package (Create or Update)
  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || busy) return;

    const name = formName.trim();
    if (!name || name.length < 2) {
      setFormError("Package name must be at least 2 characters.");
      return;
    }

    const price = parseFloat(formPrice);
    if (isNaN(price) || price < 0) {
      setFormError("Price must be 0 or a positive number. No negative price allowed.");
      return;
    }

    const durationMonths = parseInt(formDurationMonths, 10);
    if (isNaN(durationMonths) || durationMonths < 1) {
      setFormError("Duration must be at least 1 month. No zero duration allowed.");
      return;
    }

    if (formSemesters.length === 0) {
      setFormError("Select at least one semester (Semester 1, Semester 2, or both).");
      return;
    }

    const cleanedFeatures = formFeatures.map((f) => f.trim()).filter(Boolean);
    const displayOrder = parseInt(formDisplayOrder, 10) || 0;

    const payload: AdminPackageCreate = {
      name,
      description: formDescription.trim() || undefined,
      plan_type: formPlanType,
      semesters_covered: formSemesters.sort(),
      stream: formStream ? formStream.toLowerCase().trim() : null,
      price,
      duration_months: durationMonths,
      features: cleanedFeatures,
      is_active: formIsActive,
      display_order: displayOrder,
    };

    setBusy(true);
    setFormError("");
    try {
      if (editingPackage) {
        await api.admin.packages.update(token, editingPackage.id, payload);
        showNotice(`Package '${name}' updated successfully.`);
      } else {
        await api.admin.packages.create(token, payload);
        showNotice(`Package '${name}' created successfully.`);
      }
      setPackageModalOpen(false);
      await loadPackages();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Failed to save package");
    } finally {
      setBusy(false);
    }
  };

  // Delete Package
  const handleDeletePackage = async () => {
    if (!token || !packageDeleteTarget || busy) return;
    setBusy(true);
    try {
      await api.admin.packages.delete(token, packageDeleteTarget.id);
      showNotice(`Package '${packageDeleteTarget.name}' deleted.`);
      setPackageDeleteTarget(null);
      await loadPackages();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // Features row management
  const handleFeatureChange = (index: number, val: string) => {
    setFormFeatures((prev) => {
      const copy = [...prev];
      copy[index] = val;
      return copy;
    });
  };

  const handleAddFeatureRow = () => {
    setFormFeatures((prev) => [...prev, ""]);
  };

  const handleRemoveFeatureRow = (index: number) => {
    setFormFeatures((prev) => prev.filter((_, i) => i !== index));
  };

  // Semester checkbox toggle
  const toggleSemester = (sem: number) => {
    setFormSemesters((prev) => {
      if (prev.includes(sem)) {
        return prev.filter((s) => s !== sem);
      } else {
        return [...prev, sem];
      }
    });
  };

  // Grant Entitlement Submit
  const handleGrantEntitlement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || busy) return;

    if (!selectedStudent) {
      setGrantError("Please select a student first.");
      return;
    }

    const duration = parseInt(grantDurationMonths, 10);
    if (isNaN(duration) || duration < 1) {
      setGrantError("Duration must be at least 1 month.");
      return;
    }

    setBusy(true);
    setGrantError("");
    try {
      const res = await api.admin.entitlements.grant(token, {
        user_id: selectedStudent.id,
        stream: grantStream,
        semester: grantSemester,
        duration_months: duration,
        package_id: grantPackageId || undefined,
        reason: grantReason.trim() || "Admin manual grant",
      });
      showNotice(res.message);
      setGrantModalOpen(false);
      setSelectedStudent(null);
      setStudentSearchQuery("");
      setGrantReason("");
      await loadEntitlements();
    } catch (err) {
      setGrantError(err instanceof ApiError ? err.message : "Failed to grant entitlement");
    } finally {
      setBusy(false);
    }
  };

  // Revoke Entitlement Submit
  const handleRevokeEntitlement = async () => {
    if (!token || !revokeTarget || busy) return;
    setBusy(true);
    try {
      const res = await api.admin.entitlements.revoke(token, revokeTarget.id, {
        reason: revokeReason.trim() || "Admin manual revoke",
      });
      showNotice(res.message);
      setRevokeTarget(null);
      setRevokeReason("");
      await loadEntitlements();
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <AdminPageHeader
        title="Premium Packages & Entitlements"
        subtitle="Configure semester-based pricing tiers, bundles, and manage student entitlements."
        actions={
          activeTab === "packages" && canWrite ? (
            <button
              type="button"
              onClick={handleOpenCreatePackage}
              className="px-3.5 py-2 bg-primary text-on-accent text-xs font-semibold rounded-lg flex items-center gap-1.5 hover:opacity-90 transition shadow-xs"
            >
              <Plus size={14} /> Create package
            </button>
          ) : activeTab === "entitlements" && canWrite ? (
            <button
              type="button"
              onClick={() => {
                setSelectedStudent(null);
                setStudentSearchQuery("");
                setGrantStream("common");
                setGrantSemester(1);
                setGrantDurationMonths("1");
                setGrantPackageId("");
                setGrantReason("");
                setGrantError("");
                setGrantModalOpen(true);
              }}
              className="px-3.5 py-2 bg-primary text-on-accent text-xs font-semibold rounded-lg flex items-center gap-1.5 hover:opacity-90 transition shadow-xs"
            >
              <UserCheck size={14} /> Grant entitlement
            </button>
          ) : null
        }
      />

      {/* Success Notice Banner */}
      {successNotice && (
        <div className="bg-success/15 border border-success/30 text-success text-xs font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 size={15} className="shrink-0" />
          <span>{successNotice}</span>
        </div>
      )}

      {/* Tabs */}
      <AdminTabBar
        tabs={[
          { id: "packages", label: "Premium Packages", count: packages.length },
          { id: "entitlements", label: "Student Entitlements", count: entitlements.length },
        ]}
        active={activeTab}
        onChange={(id) => setActiveTab(id as "packages" | "entitlements")}
      />

      {/* Tab 1: Premium Packages */}
      {activeTab === "packages" && (
        <div className="space-y-4">
          <div className="bg-card rounded-lg border border-border overflow-hidden shadow-xs">
            <div className="overflow-x-auto admin-scroll-x">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground w-12">
                      Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Package Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Plan Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Stream
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Semester(s)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Students
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-primary" />
                        Loading packages...
                      </td>
                    </tr>
                  ) : packages.length === 0 ? (
                    <tr>
                      <td colSpan={10}>
                        <AdminEmptyState message="No packages found. Click 'Create package' above to add your first semester tier." />
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg, index) => {
                      const isActive = pkg.is_active ?? pkg.isActive ?? true;
                      const sems = pkg.semesters_covered || pkg.semestersCovered || [1];
                      const entCount = pkg.entitlementCount ?? 0;
                      const hasEntitlements = entCount > 0;

                      return (
                        <tr key={pkg.id} className="hover:bg-muted/30 transition-colors">
                          {/* Order controls */}
                          <td className="px-4 py-3 text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-xs w-4 text-center">{index + 1}</span>
                              {canWrite && (
                                <div className="flex flex-col">
                                  <button
                                    type="button"
                                    disabled={index === 0 || busy}
                                    onClick={() => handleMovePackage(index, "up")}
                                    className="p-0.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    title="Move up"
                                  >
                                    <ArrowUp size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={index === packages.length - 1 || busy}
                                    onClick={() => handleMovePackage(index, "down")}
                                    className="p-0.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                                    title="Move down"
                                  >
                                    <ArrowDown size={12} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Name & Description */}
                          <td className="px-4 py-3">
                            <div className="font-semibold text-foreground flex items-center gap-1.5">
                              <span>{pkg.name}</span>
                              {sems.length > 1 && (
                                <span className="text-xs font-bold px-1.5 py-0.2 rounded bg-primary/10 text-primary border border-primary/20">
                                  Bundle
                                </span>
                              )}
                            </div>
                            {pkg.description && (
                              <p className="text-xs text-muted-foreground max-w-xs truncate mt-0.5">
                                {pkg.description}
                              </p>
                            )}
                          </td>

                          {/* Plan Type */}
                          <td className="px-4 py-3 text-xs capitalize text-muted-foreground font-medium">
                            {pkg.plan_type || pkg.planType || "semester"}
                          </td>

                          {/* Stream */}
                          <td className="px-4 py-3">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border">
                              {pkg.stream ? pkg.stream.toUpperCase() : "COMMON / ANY"}
                            </span>
                          </td>

                          {/* Semesters Covered */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {sems.map((sem) => (
                                <span
                                  key={sem}
                                  className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20"
                                >
                                  Sem {sem}
                                </span>
                              ))}
                            </div>
                          </td>

                          {/* Price */}
                          <td className="px-4 py-3 font-semibold text-foreground">
                            {pkg.price} ETB
                          </td>

                          {/* Duration */}
                          <td className="px-4 py-3 text-muted-foreground text-xs font-medium">
                            {pkg.duration_months || pkg.durationMonths || 1}{" "}
                            {(pkg.duration_months || pkg.durationMonths || 1) === 1 ? "month" : "months"}
                          </td>

                          {/* Student Entitlements Count */}
                          <td className="px-4 py-3 text-xs font-semibold">
                            <span
                              className={`px-2 py-0.5 rounded-full ${
                                entCount > 0
                                  ? "bg-primary/10 text-primary border border-primary/20 font-bold"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {entCount} {entCount === 1 ? "student" : "students"}
                            </span>
                          </td>

                          {/* Active toggle */}
                          <td className="px-4 py-3">
                            {canWrite ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleTogglePackageActive(pkg)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                  isActive ? "bg-primary" : "bg-muted"
                                }`}
                                title={isActive ? "Click to deactivate" : "Click to activate"}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                    isActive ? "translate-x-4" : "translate-x-0"
                                  }`}
                                />
                              </button>
                            ) : (
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  isActive ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {isActive ? "Active" : "Inactive"}
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canWrite && (
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditPackage(pkg)}
                                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition"
                                  title="Edit package"
                                >
                                  <Pencil size={14} />
                                </button>
                              )}

                              {canWrite && (
                                <button
                                  type="button"
                                  disabled={hasEntitlements}
                                  onClick={() => setPackageDeleteTarget(pkg)}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
                                    hasEntitlements
                                      ? "text-muted-foreground/40 cursor-not-allowed"
                                      : "text-destructive hover:bg-destructive/10"
                                  }`}
                                  title={
                                    hasEntitlements
                                      ? "Cannot delete a package with existing student entitlements. Deactivate it instead."
                                      : "Delete package"
                                  }
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Student Entitlements */}
      {activeTab === "entitlements" && (
        <div className="space-y-4">
          {/* Filters */}
          <AdminFilterBar
            searchPlaceholder="Search student by name or phone..."
            searchValue={entSearch}
            onSearchChange={setEntSearch}
            filters={[
              {
                id: "status",
                label: "Status",
                value: entStatusFilter,
                options: [
                  { value: "all", label: "All statuses" },
                  { value: "active", label: "Active only" },
                  { value: "expired", label: "Expired only" },
                  { value: "revoked", label: "Revoked only" },
                ],
              },
              {
                id: "stream",
                label: "Stream",
                value: entStreamFilter,
                options: [
                  { value: "all", label: "All streams" },
                  { value: "common", label: "Common" },
                  { value: "natural", label: "Natural" },
                  { value: "social", label: "Social" },
                ],
              },
              {
                id: "semester",
                label: "Semester",
                value: entSemesterFilter,
                options: [
                  { value: "all", label: "All semesters" },
                  { value: "1", label: "Semester 1" },
                  { value: "2", label: "Semester 2" },
                ],
              },
            ]}
            onSelectChange={(id, value) => {
              if (id === "status") setEntStatusFilter(value);
              if (id === "stream") setEntStreamFilter(value);
              if (id === "semester") setEntSemesterFilter(value);
            }}
            showClear={Boolean(entSearch || entStatusFilter !== "all" || entStreamFilter !== "all" || entSemesterFilter !== "all")}
            onClear={() => {
              setEntSearch("");
              setEntStatusFilter("all");
              setEntStreamFilter("all");
              setEntSemesterFilter("all");
            }}
          />

          {/* Entitlements Table */}
          <div className="bg-card rounded-lg border border-border overflow-hidden shadow-xs">
            <div className="overflow-x-auto admin-scroll-x">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Student
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Stream
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Semester
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Package / Plan
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Starts At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Expires At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-primary" />
                        Loading entitlements...
                      </td>
                    </tr>
                  ) : entitlements.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <AdminEmptyState
                          message={
                            entSearch
                              ? "No entitlements matching your search."
                              : "No entitlements registered yet."
                          }
                        />
                      </td>
                    </tr>
                  ) : (
                    entitlements.map((ent) => {
                      const isRevoked = ent.status === "revoked";
                      const isExpired = ent.status === "expired";
                      const isActive = ent.status === "active";

                      return (
                        <tr key={ent.id} className="hover:bg-muted/30 transition-colors">
                          {/* Student Name & Phone */}
                          <td className="px-4 py-3">
                            <div className="font-semibold text-foreground">{ent.userName}</div>
                            <div className="text-xs text-muted-foreground font-mono">{ent.userPhone || "No phone"}</div>
                          </td>

                          {/* Stream */}
                          <td className="px-4 py-3">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border uppercase">
                              {ent.stream}
                            </span>
                          </td>

                          {/* Semester */}
                          <td className="px-4 py-3 font-semibold text-primary">
                            Semester {ent.semester}
                          </td>

                          {/* Package Name */}
                          <td className="px-4 py-3 text-xs text-foreground font-medium">
                            {ent.packageName}
                          </td>

                          {/* Starts At */}
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {ent.startsAt ? new Date(ent.startsAt).toLocaleDateString() : "—"}
                          </td>

                          {/* Expires At */}
                          <td className="px-4 py-3 text-xs font-medium text-foreground">
                            {ent.expiresAt ? new Date(ent.expiresAt).toLocaleDateString() : "—"}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                isActive
                                  ? "bg-success/10 text-success border border-success/30"
                                  : isRevoked
                                  ? "bg-destructive/10 text-destructive border border-destructive/30"
                                  : "bg-muted text-muted-foreground border border-border"
                              }`}
                            >
                              {isActive ? "Active" : isRevoked ? "Revoked" : "Expired"}
                            </span>
                          </td>

                          {/* Revoke Action */}
                          <td className="px-4 py-3 text-right">
                            {canWrite && isActive && (
                              <button
                                type="button"
                                onClick={() => {
                                  setRevokeTarget(ent);
                                  setRevokeReason("");
                                }}
                                className="px-2.5 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 rounded-md transition"
                              >
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Package Modal */}
      <ModalShell
        open={packageModalOpen}
        title={editingPackage ? `Edit Package: ${editingPackage.name}` : "Create Premium Package"}
        subtitle={
          editingPackage
            ? "Editing price or duration applies to future purchases only and will not alter existing student entitlements."
            : "Define a semester package or bundle with custom pricing and duration."
        }
        onClose={() => setPackageModalOpen(false)}
        wide
      >
        <form onSubmit={handleSavePackage} className="space-y-4">
          {formError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-xs font-semibold flex items-center gap-2">
              <AlertCircle size={14} className="shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {/* Name & Plan Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">
                Package Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Semester 1 Full Access"
                required
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                Plan Type <span className="text-destructive">*</span>
              </label>
              <select
                value={formPlanType}
                onChange={(e) => setFormPlanType(e.target.value as "monthly" | "semester")}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              >
                <option value="semester">Semester Pass</option>
                <option value="monthly">Monthly Access</option>
              </select>
            </div>
          </div>

          {/* Stream & Semesters Covered */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">
                Target Stream
              </label>
              <select
                value={formStream}
                onChange={(e) => setFormStream(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              >
                <option value="">Any Stream / Common</option>
                <option value="natural">Natural Science</option>
                <option value="social">Social Science</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Semesters Covered <span className="text-destructive">*</span>
              </label>
              <div className="flex items-center gap-4 mt-2">
                <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formSemesters.includes(1)}
                    onChange={() => toggleSemester(1)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>Semester 1</span>
                </label>
                <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formSemesters.includes(2)}
                    onChange={() => toggleSemester(2)}
                    className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                  />
                  <span>Semester 2</span>
                </label>
                {formSemesters.includes(1) && formSemesters.includes(2) && (
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                    Bundle
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Price & Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">
                Price in ETB <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
                placeholder="499"
                required
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
              />
              <p className="text-xs text-muted-foreground mt-0.5">No negative price allowed.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Duration in Months <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="24"
                value={formDurationMonths}
                onChange={(e) => setFormDurationMonths(e.target.value)}
                placeholder="1"
                required
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
              />
              <p className="text-xs text-muted-foreground mt-0.5">No zero duration allowed (minimum 1 month).</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold mb-1">Description (optional)</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={2}
              placeholder="e.g. Unlocks all freshman natural science courses and past exams for Semester 1."
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
            />
          </div>

          {/* Dynamic Feature Rows */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold">Features / Highlights</label>
              <button
                type="button"
                onClick={handleAddFeatureRow}
                className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
              >
                <Plus size={12} /> Add feature line
              </button>
            </div>
            <div className="space-y-2">
              {formFeatures.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={feat}
                    onChange={(e) => handleFeatureChange(idx, e.target.value)}
                    placeholder={`Feature line ${idx + 1}`}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-xs"
                  />
                  {formFeatures.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFeatureRow(idx)}
                      className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                      title="Remove line"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Active status & Display order */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                />
                <span>Active (visible to students)</span>
              </label>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-semibold">Display Order:</label>
              <input
                type="number"
                value={formDisplayOrder}
                onChange={(e) => setFormDisplayOrder(e.target.value)}
                className="w-16 px-2 py-1 rounded border border-border bg-background text-foreground text-xs font-mono text-center"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2 pt-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => setPackageModalOpen(false)}
              className="px-4 py-2 border border-border rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 bg-primary text-on-accent rounded-lg text-xs font-semibold hover:opacity-90 flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              {editingPackage ? "Save changes" : "Create package"}
            </button>
          </div>
        </form>
      </ModalShell>

      {/* Grant Entitlement Modal */}
      <ModalShell
        open={grantModalOpen}
        title="Grant Manual Student Entitlement"
        subtitle="Manually activate semester access for a student for support or manual payment resolution."
        onClose={() => setGrantModalOpen(false)}
      >
        <form onSubmit={handleGrantEntitlement} className="space-y-4">
          {grantError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-xs font-semibold flex items-center gap-2">
              <AlertCircle size={14} className="shrink-0" />
              <span>{grantError}</span>
            </div>
          )}

          {/* Student Search & Select */}
          <div>
            <label className="block text-xs font-semibold mb-1">
              Select Student <span className="text-destructive">*</span>
            </label>
            {selectedStudent ? (
              <div className="p-3 rounded-lg border border-primary/30 bg-primary/10 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-foreground">{selectedStudent.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{selectedStudent.phoneRaw || selectedStudent.phone}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedStudent(null)}
                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground text-xs font-semibold"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={studentSearchQuery}
                  onChange={(e) => setStudentSearchQuery(e.target.value)}
                  placeholder="Type student name or phone number..."
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                />
                {searchingStudents && (
                  <Loader2 size={14} className="animate-spin absolute right-3 top-3 text-muted-foreground" />
                )}

                {studentSearchResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto admin-scroll-y bg-card border border-border rounded-lg shadow-lg divide-y divide-border">
                    {studentSearchResults.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setSelectedStudent(u);
                          setStudentSearchQuery("");
                          setStudentSearchResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-xs flex items-center justify-between"
                      >
                        <div>
                          <span className="font-semibold text-foreground">{u.name}</span>
                          <span className="text-muted-foreground ml-2 font-mono">{u.phoneRaw || u.phone}</span>
                        </div>
                        <span className="text-xs uppercase font-bold text-primary">Select</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stream & Semester */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Stream</label>
              <select
                value={grantStream}
                onChange={(e) => setGrantStream(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              >
                <option value="common">Common</option>
                <option value="natural">Natural</option>
                <option value="social">Social</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Semester</label>
              <select
                value={grantSemester}
                onChange={(e) => setGrantSemester(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
              >
                <option value={1}>Semester 1</option>
                <option value={2}>Semester 2</option>
              </select>
            </div>
          </div>

          {/* Duration in Months */}
          <div>
            <label className="block text-xs font-semibold mb-1">
              Duration in Months <span className="text-destructive">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={grantDurationMonths}
              onChange={(e) => setGrantDurationMonths(e.target.value)}
              placeholder="1"
              required
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono"
            />
          </div>

          {/* Associated Package (Optional) */}
          <div>
            <label className="block text-xs font-semibold mb-1">
              Associated Package (optional)
            </label>
            <select
              value={grantPackageId}
              onChange={(e) => setGrantPackageId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
            >
              <option value="">None (Custom manual grant)</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name} ({pkg.price} ETB)
                </option>
              ))}
            </select>
          </div>

          {/* Reason for Audit Log */}
          <div>
            <label className="block text-xs font-semibold mb-1">
              Reason / Support Case Note <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              placeholder="e.g. Telebirr manual bank transfer verified by admin"
              required
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
            />
            <p className="text-xs text-muted-foreground mt-0.5">
              This reason will be recorded permanently in the Admin Audit Log.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => setGrantModalOpen(false)}
              className="px-4 py-2 border border-border rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !selectedStudent}
              className="px-4 py-2 bg-primary text-on-accent rounded-lg text-xs font-semibold hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : null}
              Grant Entitlement
            </button>
          </div>
        </form>
      </ModalShell>

      {/* Revoke Entitlement Modal */}
      {revokeTarget && (
        <ConfirmDialog
          open={Boolean(revokeTarget)}
          title="Revoke Student Entitlement"
          description={
            <div className="space-y-3">
              <p>
                Are you sure you want to revoke <strong>Semester {revokeTarget.semester} ({revokeTarget.stream})</strong> access for{" "}
                <strong>{revokeTarget.userName}</strong>?
              </p>
              <div>
                <label className="block text-xs font-semibold mb-1">Reason for revocation:</label>
                <input
                  type="text"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="e.g. User requested refund / disputed transaction"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                />
                <p className="text-xs text-muted-foreground mt-0.5">
                  This action will be logged to AdminAuditLog.
                </p>
              </div>
            </div>
          }
          danger
          confirmLabel="Revoke Access"
          loading={busy}
          onConfirm={handleRevokeEntitlement}
          onClose={() => setRevokeTarget(null)}
        />
      )}

      {/* Package Delete Confirmation */}
      {packageDeleteTarget && (
        <ConfirmDialog
          open={Boolean(packageDeleteTarget)}
          title={`Delete Package: ${packageDeleteTarget.name}`}
          description={`Are you sure you want to delete this package? This package has no existing entitlements and can be safely removed.`}
          danger
          confirmLabel="Delete Package"
          loading={busy}
          onConfirm={handleDeletePackage}
          onClose={() => setPackageDeleteTarget(null)}
        />
      )}
    </div>
  );
}
